import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole } from "@/lib/accountRole";
import { api, formatError } from "@/lib/api";
import { toast } from "@/lib/toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LayoutDashboard, LogOut, Repeat, Lock, Check, ChevronDown, ChevronRight, AlertTriangle, Star, Loader2 } from "lucide-react";

const AMBER = "#C2600F";
const GOLD = "#E8C46A";

const SIZE_CLS = { 8: "w-8 h-8", 9: "w-9 h-9", 10: "w-10 h-10" };
function Avatar({ src, name, username, size = 9 }) {
  const initials = (name || username || "?").slice(0, 2).toUpperCase();
  const cls = SIZE_CLS[size] || SIZE_CLS[9];
  if (src) return <img src={src} alt="" className={`${cls} object-cover border border-sniper shrink-0`} />;
  return <div className={`${cls} shrink-0 bg-olive/20 border border-olive/40 flex items-center justify-center font-heading text-xs text-olive-bright`}>{initials}</div>;
}

function RoleBadge({ role }) {
  const admin = isAdminRole(role);
  return (
    <span className={`font-mono text-[9px] uppercase px-1 py-0.5 border ${admin ? "text-red-400 border-red-500/50" : "text-secondary-foreground border-sniper"}`}>
      {admin ? "admin" : "member"}
    </span>
  );
}

export default function AccountMenu() {
  const { user, applyAuth, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [links, setLinks] = useState(null);
  const [stepUp, setStepUp] = useState(null); // { target }
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [founding, setFounding] = useState(null);
  const [resending, setResending] = useState(false);
  const ref = useRef(null);

  const loadLinks = useCallback(() => {
    api.get("/account/links").then(({ data }) => setLinks(data)).catch(() => setLinks({ has_links: false, is_primary: true, accounts: [] }));
  }, []);
  useEffect(() => { if (user) loadLinks(); }, [user, loadLinks]);
  // Permanent "claim founding seat" menu link — shown while a seat is claimable (ignores dismiss).
  useEffect(() => {
    if (user) api.get("/founding/prompt").then(({ data }) => setFounding(data)).catch(() => setFounding(null));
  }, [user]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const doSwitch = async (target, password) => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/switch", { target_user_id: target.user_id, password });
      applyAuth(data.token, data.user);
      setStepUp(null); setPw(""); setOpen(false);
      toast.success(`Switched to @${data.user.username}`);
      navigate("/dashboard");
      setTimeout(loadLinks, 300);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === "step_up_required") {
        setStepUp({ target });
      } else {
        toast.error(formatError(detail));
      }
    } finally { setBusy(false); }
  };

  const resend = async () => {
    setResending(true);
    try { await api.post("/auth/send-verification"); toast.success("Verification email sent — check your inbox (and spam)."); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setResending(false); }
  };

  if (!user) return null;
  const others = (links?.accounts || []).filter((a) => !a.is_current);
  const notPrimary = links?.has_links && links?.is_primary === false;
  const emailUnverified = user.email_verified === false;
  const showFounding = !!(founding && founding.eligible && !founding.has_seat && (founding.public_remaining || 0) > 0);

  return (
    <div className="relative flex items-center gap-2" ref={ref}>
      {notPrimary && (
        <span data-testid="acting-as-indicator" title="You are acting as a switched-in account"
          className="hidden sm:inline-flex items-center gap-1 font-mono text-[10px] uppercase px-2 py-1 border border-volt/50 text-volt bg-volt/5">
          <Repeat className="w-3 h-3" /> Acting as @{user.username}
        </span>
      )}
      <button onClick={() => setOpen((o) => !o)} data-testid="account-menu-trigger"
        className="flex items-center gap-1 pl-1 pr-1.5 py-1 border border-transparent hover:border-sniper transition-colors">
        <Avatar src={user.avatar_thumb || user.avatar} name={user.name} username={user.username} size={8} />
        <ChevronDown className="w-3.5 h-3.5 text-secondary-foreground" />
      </button>

      {open && (
        <div data-testid="account-menu" className="absolute right-0 top-full mt-2 w-72 bg-surface border border-sniper shadow-xl z-50 animate-fade-up max-h-[calc(100vh-5rem)] overflow-y-auto">
          {/* 1. VERIFY EMAIL — actionable row with RESEND (not a dead chip); only while unverified */}
          {emailUnverified && (
            <div data-testid="menu-verify-row" className="px-3 py-3 border-b border-sniper" style={{ background: "rgba(194,96,15,0.10)", borderLeft: `4px solid ${AMBER}` }}>
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#E8A05A" }} />
                <div className="min-w-0">
                  <p className="font-mono text-[12px] leading-snug" style={{ color: "#E8A05A" }}>Verify your email to buy, sell and message.</p>
                  <button onClick={resend} disabled={resending} data-testid="menu-verify-resend"
                    className="mt-1.5 inline-flex items-center gap-1.5 font-mono uppercase text-[11px] font-bold tracking-wide underline underline-offset-2 disabled:opacity-60" style={{ color: "#E8A05A" }}>
                    {resending && <Loader2 className="w-3 h-3 animate-spin" />} Resend the email
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 2. IDENTITY ROW — handle at top + the exact words "MY PROFILE & BADGES" → /profile (mobile parity) */}
          <button onClick={() => { setOpen(false); navigate("/profile"); }}
            data-testid="account-menu-current" title="Your profile & badges"
            className="w-full text-left p-3 border-b border-sniper flex items-center gap-3 hover:bg-white/5 transition-colors group">
            <Avatar src={user.avatar_thumb || user.avatar} name={user.name} username={user.username} size={10} />
            <div className="min-w-0">
              <div className="font-heading text-foreground truncate flex items-center gap-1.5">@{user.username} <RoleBadge role={user.role} /></div>
              <div className="font-mono uppercase text-[11px] tracking-wide text-olive-bright inline-flex items-center gap-1 mt-0.5 group-hover:text-volt transition-colors">My profile &amp; badges <ChevronRight className="w-3.5 h-3.5" /></div>
            </div>
          </button>

          {/* 3. FOUNDING SEAT ROW — gold, live remaining count; only while claimable + seatless + seats remain */}
          {showFounding && (
            <button onClick={() => { setOpen(false); navigate("/founding"); }} data-testid="menu-founding"
              className="w-full flex items-center justify-between gap-3 px-3 py-2.5 border-b border-sniper text-left hover:bg-[#E8C46A]/10 transition-colors" style={{ borderLeftWidth: 4, borderLeftColor: GOLD }}>
              <span className="font-mono uppercase text-sm inline-flex items-center gap-2" style={{ color: GOLD }}><Star className="w-4 h-4 fill-current" /> Claim founding seat</span>
              <span className="font-mono text-xs tabular-nums shrink-0" data-testid="menu-founding-remaining" style={{ color: GOLD }}>{founding.public_remaining} left</span>
            </button>
          )}

          {/* 4. SWITCH ACCOUNT */}
          {others.length > 0 && (
            <div className="border-b border-sniper py-1" data-testid="switch-account-list">
              <div className="px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><Repeat className="w-3 h-3" /> Switch account</div>
              {others.map((a) => (
                <button key={a.user_id} onClick={() => doSwitch(a)} disabled={busy} data-testid={`switch-to-${a.username}`}
                  className="w-full flex items-center gap-3 px-3 py-2 hover:bg-white/5 transition-colors text-left">
                  <Avatar src={a.avatar_thumb || a.avatar} name={a.name} username={a.username} size={8} />
                  <div className="min-w-0 flex-1">
                    <div className="font-body text-sm text-foreground truncate flex items-center gap-1.5">{a.name || a.username} <RoleBadge role={a.role} />{a.is_primary && <span className="font-mono text-[9px] uppercase text-olive-bright">primary</span>}</div>
                    <div className="font-mono text-[10px] text-muted-foreground truncate">@{a.username}</div>
                  </div>
                  {a.requires_step_up && <Lock className="w-3.5 h-3.5 text-volt shrink-0" title="Requires verification" />}
                </button>
              ))}
            </div>
          )}

          {/* 5. MEMBER ITEMS — above marketing */}
          <div className="py-1">
            <button onClick={() => { setOpen(false); navigate("/dashboard"); }} data-testid="menu-dashboard" className="w-full flex items-center gap-2.5 px-3 py-2 text-sm font-mono uppercase text-secondary-foreground hover:text-foreground hover:bg-white/5 transition-colors"><LayoutDashboard className="w-4 h-4" /> Dashboard</button>
            <button onClick={() => { setOpen(false); navigate("/settings/security"); }} data-testid="menu-security" className="w-full flex items-center gap-2.5 px-3 py-2 text-sm font-mono uppercase text-secondary-foreground hover:text-foreground hover:bg-white/5 transition-colors"><Lock className="w-4 h-4" /> Password &amp; Security</button>
          </div>

          {/* 6. SIGN OUT — last, separated */}
          <div className="border-t border-sniper py-1">
            <button onClick={() => { setOpen(false); logout(); navigate("/"); }} data-testid="menu-signout" className="w-full flex items-center gap-2.5 px-3 py-2 text-sm font-mono uppercase text-blaze hover:bg-blaze/10 transition-colors"><LogOut className="w-4 h-4" /> Sign out</button>
          </div>
        </div>
      )}

      {/* step-up challenge for privilege-escalating switches */}
      <Dialog open={!!stepUp} onOpenChange={(o) => { if (!o) { setStepUp(null); setPw(""); } }}>
        <DialogContent className="bg-surface border-sniper text-foreground max-w-sm" data-testid="stepup-dialog">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight flex items-center gap-2"><Lock className="w-5 h-5 text-volt" /> Verify to switch</DialogTitle></DialogHeader>
          <p className="text-sm font-body text-secondary-foreground">
            Switching to <span className="text-foreground">@{stepUp?.target?.username}</span> grants higher privileges. Re-enter your current password to continue.
          </p>
          <form onSubmit={(e) => { e.preventDefault(); if (pw) doSwitch(stepUp.target, pw); }} className="mt-3 space-y-3">
            <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} autoFocus data-testid="stepup-password"
              placeholder="Current password" className="w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2.5 text-foreground font-mono text-sm" />
            <button type="submit" disabled={busy || !pw} data-testid="stepup-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-olive hover:bg-olive-bright disabled:opacity-50 text-white font-heading font-bold uppercase py-2.5 transition-colors">
              <Check className="w-4 h-4" /> {busy ? "Verifying…" : "Verify & switch"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
